import { INode, useCreateDirectoryMutation } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { FolderEdit, FolderPlus } from 'lucide-react'
import { buildNodePath, useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { useCallback, useState } from 'react'
import { NodeItemDirectoryIcon } from '@/features/drive/entities/node/node-item-directory/node-item-directory-icon'
import { InputGroup } from '@/components/form/input-group'
import z from 'zod'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { DirectoryColorPicker } from '@/features/drive/functionalities/actions/node/core/shared/directory-create-or-update-modal/directory-color-picker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { EmojiPicker } from '@ferrucc-io/emoji-picker'
import { GenericFormField } from '@/components/form/generic-form-field'
import { toast } from '@/features/toast/toast'
import { useRenameNodeMutation } from '@/api/nodes/mutations/rename-node-mutation'

const CREATE_OR_UPDATE_DIRECTORY_SCHEMA = z.object({
    name: z.string().min(1, 'Name is required'),
    emoji: z.string().optional(),
    color: z.string(),
})

type IDirectoryCreateDirectoryFormData = z.infer<typeof CREATE_OR_UPDATE_DIRECTORY_SCHEMA>

type IDirectoryCreateOrUpdateModalProps = {
    directory: INode | undefined
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * When no directory is provided, we create a new one.
 * When a directory is provided, we update it.
 */
export const DirectoryCreateOrUpdateModal = ({ directory, open, onOpenChange }: IDirectoryCreateOrUpdateModalProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const { nodePath } = useDirectoryContext()

    const { mutateAsync: asyncCreateDirectory, isPending } = useCreateDirectoryMutation()
    const { mutateAsync: asyncUpdateDirectory, isPending: isUpdatePending } = useRenameNodeMutation()

    const form = useForm<IDirectoryCreateDirectoryFormData>({
        resolver: zodResolver(CREATE_OR_UPDATE_DIRECTORY_SCHEMA),
        defaultValues: {
            name: directory?.name ?? '',
            emoji: directory?.emoji ?? '',
            color: directory?.color ?? '#649EF4',
        },
    })

    const handleCreateDirectory = useCallback(async () => {
        const isValid = await form.trigger()

        if (!isValid) {
            toast.error('Please fill in all fields')

            return
        }

        const values = form.getValues()

        if (directory) {
            await asyncUpdateDirectory({
                nodePath: buildNodePath(nodePath, directory.slug),
                newName: values.name,
                newEmoji: values.emoji,
                newColor: values.color,
            })
        } else {
            await asyncCreateDirectory({
                name: values.name,
                parentNodePath: nodePath,
                emoji: values.emoji,
                color: values.color,
            })
        }

        form.reset()
        onOpenChange(false)
    }, [asyncCreateDirectory, form, nodePath, onOpenChange, asyncUpdateDirectory, directory])

    const properties = {
        title: directory ? 'Update Folder' : 'Create Folder',
        description: directory
            ? 'Update the folder in the current directory'
            : 'Create a new folder in the current directory',
        buttonLabel: directory ? 'Update Folder' : 'Create Folder',
        buttonLoading: isPending || isUpdatePending,
        buttonDisabled: isPending || isUpdatePending,
    }

    return (
        <GeneralModal
            open={open}
            onOpenChange={onOpenChange}
            title={properties.title}
            subtitle={properties.description}
            icon={
                <IconBox>
                    {directory ? (
                        <FolderEdit className="h-5 w-5 text-stone-500" />
                    ) : (
                        <FolderPlus className="h-5 w-5 text-stone-500" />
                    )}
                </IconBox>
            }
            className="md:w-[540px]"
        >
            <div className="grid grid-cols-[200px_1fr] gap-6">
                <div className="bg-[#FAFAFA] flex w-full h-full items-center justify-center relative rounded-lg">
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <PopoverTrigger asChild>
                            <button type="button" onClick={() => setIsPopoverOpen(true)}>
                                <NodeItemDirectoryIcon
                                    color={form.watch('color')}
                                    scale={2}
                                    mode="upload"
                                    emoji={form.watch('emoji')}
                                />
                            </button>
                        </PopoverTrigger>

                        <PopoverContent className="w-auto p-0 max-h-[300px] overflow-y-auto">
                            <EmojiPicker
                                className="bg-white"
                                onEmojiSelect={(emoji) => {
                                    setIsPopoverOpen(false)

                                    form.setValue('emoji', emoji)
                                }}
                            >
                                <EmojiPicker.Header>
                                    <EmojiPicker.Input placeholder="Search emoji" />
                                </EmojiPicker.Header>
                                <EmojiPicker.Group>
                                    <EmojiPicker.List />
                                </EmojiPicker.Group>
                            </EmojiPicker>
                        </PopoverContent>
                    </Popover>
                </div>

                <FormProvider {...form}>
                    <div className="w-full">
                        <InputGroup
                            required
                            autoFocus
                            placeholder="e.g Summer Campaign"
                            type="text"
                            label="Folder name"
                            containerClassName="mb-3 w-full"
                            onSubmit={handleCreateDirectory}
                            {...form.register('name')}
                        />

                        <GenericFormField
                            name="color"
                            label="Folder color"
                            render={({ field }) => (
                                <DirectoryColorPicker value={field.value} onChange={field.onChange} />
                            )}
                        />

                        <div className="flex justify-end gap-2 mt-8 w-full">
                            <Button
                                variant="ds2-secondary"
                                size="ds2"
                                onClick={() => onOpenChange(false)}
                                className="w-full"
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="ds2-primary"
                                size="ds2"
                                onClick={handleCreateDirectory}
                                isLoading={properties.buttonLoading}
                                disabled={properties.buttonDisabled}
                                className="w-full"
                            >
                                {properties.buttonLabel}
                            </Button>
                        </div>
                    </div>
                </FormProvider>
            </div>
        </GeneralModal>
    )
}
